import { LinkOutlined, PlusOutlined } from '@ant-design/icons'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { ReadableDifference, ShowRunOutWarningClass, TankHasWarning } from '@utils/general'
import { Button, Card, Col, Row, Tooltip } from 'antd'
import React, { useMemo } from 'react'

import { FlagIcon } from '../Flags/FlagIcon'
import { TankFillBar } from './TankFillBar'
import { StorageDetailVolume } from './StorageDetailVolume'
import type { ProductGroupsApiType } from '@/components/OrderWizard/Contexts/OrderContainersContext/ProductGroups/useProductGroups'

type TankProductRowProps = {
  rundownToggled?: boolean
  tank: Gravitate.Tank
  isSelected?: boolean
  setSelectedTank?: (tank: Gravitate.Tank) => void
  addFunction?: (tank: Gravitate.Tank) => void
  onOrder?: boolean
  disabled?: boolean
  hideRunout?: boolean
  callInSite?: boolean
  currentEntryMode?: 'by_compartment' | 'by_product'
  productGroupsApi?: ProductGroupsApiType
  isManifold?: boolean
}

export function TankProductRow({
  rundownToggled,
  tank,
  isSelected,
  setSelectedTank,
  addFunction,
  onOrder = false,
  disabled,
  hideRunout,
  callInSite,
  currentEntryMode,
  productGroupsApi,
  isManifold,
}: TankProductRowProps) {
  const showTankFlags = !callInSite && tank.flags && tank.flags.length > 0
  const runoutDate = rundownToggled ? tank.run_out_date_with_drops : tank.run_out_date

  const isAddDisabled = useMemo(() => {
    if (currentEntryMode === 'by_compartment' || !productGroupsApi)
      return { canAdd: !disabled, reason: 'All compartments are allocated.' }
    return productGroupsApi.canAddProduct(tank)
  }, [productGroupsApi, productGroupsApi?.productGroups, disabled])

  return (
    <Card
      className={`m-2 store-details-product-container ${isSelected ? ' selected' : ''} ${isManifold ? 'store-details-manifold' : ''}`}
      size='small'
    >
      <Horizontal justifyContent='flex-end'>
        {addFunction && (
          <div>
            <Tooltip title={!isAddDisabled.canAdd && isAddDisabled.reason}>
              <Button
                className='tank-product-add'
                data-cy='tank-product-add'
                size='small'
                shape='circle'
                disabled={!isAddDisabled.canAdd}
                icon={<PlusOutlined />}
                onClick={() => addFunction(tank)}
              />
            </Tooltip>
          </div>
        )}
        <Vertical onClick={() => setSelectedTank?.(tank)}>
          <Horizontal alignItems='flex-end'>
            <Vertical verticalCenter className={`store-detail-product-title ${onOrder ? ' order' : ''}`}>
              <Horizontal verticalCenter>
                {showTankFlags && <FlagIcon flags={tank.flags} />}
                <Vertical flex={1}>
                  <Texto className='text-ellipsis' category='p2' weight='bold'>
                    {tank.product}
                  </Texto>

                  <Horizontal verticalCenter justifyContent='space-between'>
                    <Texto className='text-ellipsis' align='left' category='label' style={{ flexShrink: 0 }}>
                      {isManifold && <LinkOutlined className='pr-1' />} TANK {tank?.tank_id}
                    </Texto>
                    {tank.description && (
                      <Tooltip title={tank.description} trigger={'hover'}>
                        <Texto className='text-ellipsis' category='label'>
                          {tank.description}
                        </Texto>
                      </Tooltip>
                    )}
                    {!hideRunout && !callInSite && (
                      <Texto align='right' className={`text-ellipsis ${ShowRunOutWarningClass(runoutDate)}`}>
                        {ReadableDifference(runoutDate, undefined, true, false, true)}
                      </Texto>
                    )}
                  </Horizontal>
                </Vertical>
              </Horizontal>
            </Vertical>
            {callInSite && (
              <Vertical flex={1} verticalCenter horizontalCenter>
                <Texto category='label' appearance={'hint'}>
                  Max Volume
                </Texto>
                <Texto textTransform={'uppercase'}>{addCommasToNumber(tank.storage_max, 0)}</Texto>
              </Vertical>
            )}
          </Horizontal>
          <Vertical>
            {!callInSite && (
              <TankFillBar
                fill={tank.percent_filled}
                showWarning={runoutDate && !hideRunout ? TankHasWarning(runoutDate) : false}
                projectedFill={tank.projected_percent_filled}
                lowThreshold={0.5}
              />
            )}
          </Vertical>
          <Vertical>
            {!callInSite && (
              <StorageDetailVolume
                current_volume={addCommasToNumber(tank.current_volume, 0)}
                projected_volume={tank.projected_volume && addCommasToNumber(tank.projected_volume, 0)}
                storage_max={addCommasToNumber(tank.storage_max, 0)}
              />
            )}
          </Vertical>
        </Vertical>
      </Horizontal>
    </Card>
  )
}
